import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { isPending, type StagedAttachment } from '../drafts/draftUploads';
import { useTaskDraftStore } from '../drafts/taskDraftStore';
import { taskKeys } from '../queryKeys';

export {
  ATTACHMENT_UPLOAD_CONCURRENCY,
  type AttachmentStatus,
  type StagedAttachment,
} from '../drafts/draftUploads';

const NONE: StagedAttachment[] = [];

/**
 * One upload queue, read from the draft store (spec 120, moved by spec 121).
 *
 * `queueId` is a draft's key for the Create dialog, or `task:<id>` for a
 * task's drawer. The queue itself lives in the store so it outlives the
 * component that started it; this is the view of it a component needs.
 */
export function useAttachmentUploads(workspaceId: string, queueId: string) {
  const queryClient = useQueryClient();
  const items = useTaskDraftStore((s) => s.queues[queueId]?.items ?? NONE);
  const stageFiles = useTaskDraftStore((s) => s.stageFiles);
  const removeFile = useTaskDraftStore((s) => s.removeFile);
  const retryUpload = useTaskDraftStore((s) => s.retryUpload);
  const startUploads = useTaskDraftStore((s) => s.startUploads);

  const add = useCallback(
    (files: readonly File[], options?: { replace?: boolean }) => {
      stageFiles(queueId, files, options);
    },
    [queueId, stageFiles],
  );
  const remove = useCallback(
    (id: string) => {
      removeFile(queueId, id);
    },
    [queueId, removeFile],
  );
  const retry = useCallback(
    (id: string) => {
      retryUpload(queueId, id);
    },
    [queueId, retryUpload],
  );
  const startFor = useCallback(
    (taskId: string) => {
      startUploads(queueId, {
        workspaceId,
        taskId,
        onUploaded: () =>
          void queryClient.invalidateQueries({
            queryKey: taskKeys.attachments(workspaceId, taskId),
          }),
      });
    },
    [queryClient, queueId, startUploads, workspaceId],
  );

  const inProgress = items.some(isPending);
  const failed = items.filter((item) => item.status.kind === 'failed');

  return { items, add, remove, retry, startFor, inProgress, failed };
}
